import { useEffect, useMemo, type ReactElement } from 'react';
import { CreateInterviewBody } from '@synergetx/puppet-interview-types';
import { Field, useField } from 'formik';
import groupBy from 'lodash/groupBy';
import { useLatest } from 'react-use';
import ClearIcon from '@mui/icons-material/Clear';
import {
    Box,
    FormControl,
    IconButton,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';

import { useInterviewOptions } from '@providers/InterviewsProvider';
import { log } from '@modules/logging';

/**
 * Edits new interview options in FormikState
 * see: CreateInterviewBody for available values to edit
 * @returns interview creation editor
 */
export default function CreateInterviewInput() {
    const { allInterviews } = useInterviewOptions();

    const [{ value: baseInterviewId }, , { setValue: setBaseInterviewId }] =
        useField<CreateInterviewBody['baseInterviewId']>('baseInterviewId');
    const [{ value: baseGroupId }, , { setValue: setBaseGroupId }] =
        useField<CreateInterviewBody['baseGroupId']>('baseGroupId');
    const [{ value: name }, , { setValue: setName }] =
        useField<CreateInterviewBody['name']>('name');

    const onSelectInterview = useLatest((interviewId: string, groupId: string) => {
        const interviewToDuplicate = allInterviews?.find(
            ({ id, groupId }) => id === interviewId && groupId === groupId,
        );

        if (interviewToDuplicate) {
            setBaseInterviewId(interviewId);
            setBaseGroupId(groupId);

            // if the user hasn't edited the "name" field, blow it out too
            if (!name || name.toUpperCase().includes('COPY')) {
                setName(`${interviewToDuplicate.name} (Copy)`);
            }
        } else {
            log(`[onSelectInterview] interviewToDuplicate not found`, { interviewId, groupId });
        }
    });

    useEffect(() => {
        if (baseInterviewId && baseGroupId) {
            onSelectInterview.current(baseInterviewId, baseGroupId);
        }
    }, [baseGroupId, baseInterviewId, onSelectInterview]);

    const interviewOptions = useMemo(() => {
        if (allInterviews) {
            const options: ReactElement[] = [];
            const groupedInterviews = groupBy(allInterviews, ({ groupId }) => groupId);

            Object.keys(groupedInterviews).forEach((groupId, i) => {
                options.push(
                    <ListSubheader
                        sx={{ color: '#222', fontSize: 14, fontWeight: 900 }}
                        key={`header-${groupId}-${i}-${groupedInterviews?.[groupId]?.[0].name}`}
                    >
                        {groupId}
                    </ListSubheader>,
                );
                groupedInterviews[groupId].map((interview, i) => {
                    options.push(
                        <MenuItem
                            onClick={() =>
                                onSelectInterview.current(interview.id, interview.groupId)
                            }
                            sx={{ paddingLeft: 3 }}
                            value={interview.id}
                            key={`header-${interview.id}-${i}`}
                        >
                            {`${interview.archived ? '[ARCHIVED] - ' : ''}${interview.name}`}
                        </MenuItem>,
                    );
                });
            });
            return options;
        } else {
            return [];
        }
    }, [allInterviews, onSelectInterview]);

    return (
        <Stack spacing={2}>
            <Box>
                <Field
                    as={TextField}
                    name="name"
                    type="Name"
                    label="Name"
                    required
                    fullWidth
                    slotProps={{
                        input: {
                            placeholder: 'Enter Survey Name...',
                        },
                    }}
                />
            </Box>
            <Box>
                <FormControl fullWidth>
                    <InputLabel id="create-interview-select-label">
                        Duplicate a current survey?
                    </InputLabel>
                    <Select
                        labelId="create-interview-select-label"
                        id="create-interview-select"
                        value={baseInterviewId}
                        label="Duplicate a current survey?"
                        IconComponent={
                            baseInterviewId
                                ? () => (
                                      <IconButton
                                          size="small"
                                          style={{ marginRight: '.5rem' }}
                                          onClick={() => {
                                              setBaseInterviewId('');
                                              setBaseGroupId('');
                                          }}
                                      >
                                          <ClearIcon />
                                      </IconButton>
                                  )
                                : undefined
                        }
                    >
                        {interviewOptions}
                    </Select>
                </FormControl>
            </Box>
        </Stack>
    );
}
